/**
 * User Preferences API Service
 * Manages user preferences stored in Firestore with tenant isolation
 *
 * Firestore Structure:
 * /tenants/{tenantId}/{appId}/_app.userPreferences
 *   virtualZones: { ... }      // NEW: renamed from virtualGroups
 *   virtualGroups: { ... }     // LEGACY: kept for backward compatibility migration
 *   batteryAlert: { ... }
 *   dashboardSettings: { ... }
 *   deviceSelections: { ... }
 */

import { logger } from '../utils/logger';

const API_BASE_URL = 'https://turing.ultronsmart.com/api/v2';
const APP_ID = 'KLMGeDMy'; // Unified Boards App ID

// ============================================================
// Zone Types (renamed from Group)
// ============================================================

export interface DeviceZone {
  id: string;
  name: string;
  type: 'manual' | 'smart' | 'auto';  // Required field to match DeviceManagerSidebar
  devices: string[];
  createdAt?: string;
}

export interface VirtualZones {
  climate?: DeviceZone[];
  energy?: DeviceZone[];
  lighting?: DeviceZone[];
  hvac?: DeviceZone[];
  intelliSpace?: DeviceZone[];
}

// ============================================================
// Legacy Types (kept for backward compatibility during migration)
// ============================================================

/** @deprecated Use DeviceZone instead */
export type DeviceGroup = DeviceZone;

/** @deprecated Use VirtualZones instead */
export type VirtualGroups = VirtualZones;

// Device interface matching DeviceManagerSidebar
export interface Device {
  sn: string;
  nickname?: string;
  displayName?: string;
  name?: string;
  deviceName?: string;
  type: string;
  groupId: string;
  groupName?: string;
  status: 'online' | 'offline';
  imported?: boolean;
  selected?: boolean;
  batteryLevel?: number;
  hasBattery?: boolean;
  traits?: string[];
  attributes?: {
    sensorProfile?: {
      lux?: { Min?: number; Max?: number; Unit?: string };
      temperature?: { Min?: number; Max?: number; Unit?: string };
      humidity?: { Min?: number; Max?: number; Unit?: string };
      [key: string]: any;
    };
    detectorSensitivity?: { min?: number; max?: number };
    detectorCooldownTime?: { min?: number; max?: number };
    detectorStates?: string[];
    [key: string]: any;
  };
}

export interface ImportedDevices {
  climate?: Device[];
  energy?: Device[];
  lighting?: Device[];
  hvac?: Device[];
  intelliSpace?: Device[];
}

// Dashboard settings interface
export interface DashboardSettings {
  powerMeterRefreshInterval: number;    // Default: 30
  powerChartRefreshInterval: number;    // Default: 1800
  showOfflineDevices: boolean;          // Default: true
  autoRefresh: boolean;                 // Default: true
  demoMode: boolean;                    // Default: false
  timezone: string;                     // Default: browser timezone
  keepLoggedIn: boolean;                // Default: false - 保持登入（OAuth 自動刷新）
}

// Device order per board
export interface DeviceOrder {
  energy?: string[];
  climate?: string[];
  hvac?: string[];
  lighting?: string[];
  intelliSpace?: string[];
}

// Selected devices per board/chart
export interface SelectedDevices {
  energy?: string[];
  climate?: string[];
  power24h?: string[];
}

// IntelliSpace device selections
export interface IntelliSpaceSelections {
  powerMeters?: string[];
  switches?: string[];
  hvac?: string[];
  thSensors?: string[];
  lights?: string[];
  deviceOrder?: string[];
  excludedRankingDevices?: string[];
  baselineLightingPower?: number;  // Baseline power for lighting energy saving calculation (W)
  baselineHvacPower?: number;      // Baseline power for HVAC energy saving calculation (W)
}

// Company branding
export interface CompanyBranding {
  logo?: string;           // Base64 encoded
  favicon?: string;        // Base64 encoded
  companyName?: string;
}

// Battery alert settings
export interface BatteryAlertSettings {
  enabled: boolean;
  threshold: number;       // Default: 20
  notificationChannels?: string[];  // Default: ['websocket', 'email']
  useCustomEmail?: boolean;  // Default: false
  customEmail?: string;      // Custom email address (optional)
}

// Device settings interface (per device)
export interface DeviceSettings {
  maxCurrent?: number; // 最大電流設定 (A)
  favoriteOpenPercent?: number; // Curtain/Blind favorite position (0-100)
  [key: string]: any; // 允許擴展其他設備設定
}

// ============================================================
// Device Control Group (Apple Home style group control)
// ============================================================

/**
 * Device Control Group - Groups same-type devices within a Zone for unified control
 * Similar to Apple Home's "Group" feature
 */
export interface DeviceControlGroup {
  id: string;                    // UUID
  name: string;                  // Group name (e.g., "客廳燈控")
  zoneId: string;                // Parent Zone ID
  deviceType: string;            // Device type (light, ac, curtain, blind, switch, fan, outlet)
  devices: string[];             // deviceSNs in this group
  createdAt: string;             // ISO timestamp
}

export interface UserPreferences {
  // Device management
  virtualZones?: VirtualZones;           // NEW: renamed from virtualGroups
  virtualGroups?: VirtualGroups;         // LEGACY: kept for backward compatibility migration
  importedDevices?: ImportedDevices;

  // User settings
  dashboardSettings?: DashboardSettings;
  theme?: 'light' | 'dark';
  language?: 'zh-TW' | 'en-US';

  // Device order & selection
  deviceOrder?: DeviceOrder;
  selectedDevices?: SelectedDevices;
  
  // IntelliSpace selections
  intelliSpaceSelections?: IntelliSpaceSelections;

  // Branding
  companyBranding?: CompanyBranding;

  // Alerts
  batteryAlert?: BatteryAlertSettings;
  hvacAlerts?: any;

  // HVAC AI settings
  hvacAiSettings?: {
    [groupId: string]: any;
  };

  // Device settings (per device) - 設備自訂設定 (如最大電流)
  deviceSettings?: Record<string, DeviceSettings>; // key: deviceSN, value: DeviceSettings

  // Device categories - legacy field (kept for backward compatibility, no longer actively used)
  // All devices are now managed under intelliSpace
  deviceCategories?: Record<string, string>; // key: deviceSN, value: category

  // Multi-channel device display mode - per-device preference
  // 'merged' = show as single card with expandable sub-channels (default)
  // 'expanded' = show each channel as independent card
  multiChannelDisplayMode?: Record<string, 'merged' | 'expanded'>; // key: baseSN

  // Device Control Groups (Apple Home style group control)
  deviceControlGroups?: DeviceControlGroup[];

  // Device Modal Themes - per-device-type UI configuration
  // Stores template and style preferences for device control modals
  deviceModalThemes?: {
    [deviceType: string]: {
      templateId: string;
      templateName?: string; // Custom display name for the template (user-editable)
      traitStyles?: {
        OnOff?: string;
        Brightness?: string;
        HumiditySetting?: string;
        FanSpeed?: string;
        Mode?: string;
        Timer?: string;
        CustomToggles?: string;
        TemperatureSetting?: string;
        ThermostatMode?: string;
        OpenClose?: string;
        ColorSetting?: string;
      };
      attributeStyles?: {
        SensorDisplay?: string;
        SensorChart?: string;
        StatusIndicator?: string;
        OccupancyTimeline?: string;
      };
      iconStyle?: string; // Legacy: Device icon style (e.g., 'Style01', 'Style02')
      smallIconStyle?: string; // Small icon style (title bar)
      bigIconStyle?: string; // Big icon style (main display)
      traitOrder?: string[]; // Custom order of trait/attribute components
      hiddenComponents?: string[]; // List of hidden component IDs
    };
  };

  // Allow additional fields
  [key: string]: any;
}

// ============================================================
// Preferences Cache (reduces redundant API calls)
// ============================================================
let preferencesCache: { data: UserPreferences; timestamp: number } | null = null;
let pendingRequest: Promise<UserPreferences> | null = null;
const CACHE_TTL = 5000; // 5 seconds

/**
 * Invalidate the preferences cache
 * Call this after any save operation to ensure fresh data on next read
 */
export function invalidatePreferencesCache(): void {
  preferencesCache = null;
  pendingRequest = null;
}

/**
 * Get OAuth access token from session storage
 */
function getAccessToken(): string | null {
  try {
    const tokensStr = sessionStorage.getItem('ultron_tokens');
    if (!tokensStr) return null;

    const tokens = JSON.parse(tokensStr);
    return tokens.accessToken || null;
  } catch (error) {
    logger.error('Failed to get access token:', error);
    return null;
  }
}

/**
 * Decode JWT token payload (without verification)
 * Returns the decoded payload or null if invalid
 * Note: Silently fails for non-JWT tokens (e.g., opaque tokens)
 */
function decodeJWTPayload(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      // Not a JWT token - silently return null (might be opaque token)
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    // Silently fail - token might not be JWT format
    return null;
  }
}

/**
 * Get tenant ID from access token
 */
function getTenantIdFromToken(): string | null {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      // No token available - silently return null (expected when logged out)
      return null;
    }

    const payload = decodeJWTPayload(accessToken);
    if (!payload) {
      // Token is not JWT format (likely opaque token) - this is normal and expected
      return null;
    }

    // The tenant ID should be in the 'sub' (subject) or 'userId' field
    const tenantId = payload.sub || payload.userId || payload.user_id;
    if (!tenantId) {
      // No tenant ID in token - not an error, just means validation not possible
      return null;
    }

    return tenantId;
  } catch (error) {
    // Silently fail - this is an optional validation feature
    return null;
  }
}

/**
 * Get user preferences from Firestore
 * Uses caching to reduce redundant API calls:
 * - 5 second TTL cache
 * - Request deduplication (concurrent calls share same Promise)
 */
export async function getUserPreferences(): Promise<UserPreferences> {
  // 1. Check cache (5 second TTL)
  if (preferencesCache && Date.now() - preferencesCache.timestamp < CACHE_TTL) {
    return preferencesCache.data;
  }

  // 2. Request deduplication - return pending request if exists
  if (pendingRequest) {
    return pendingRequest;
  }

  // 3. Make new request
  pendingRequest = (async () => {
    try {
      let accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      let response = await fetch(`${API_BASE_URL}/user-preferences`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Ultron-Cloud-Appid': APP_ID,
        'Content-Type': 'application/json'
      }
    });

    // Handle 401 - attempt automatic token refresh
    if (response.status === 401) {
      logger.warn('⚠️ 401 Unauthorized in getUserPreferences - Attempting token refresh...');
      const { authService } = await import('./authService');
      const refreshed = await authService.refreshToken();

      if (refreshed) {
        // Retry with new token
        accessToken = getAccessToken();
        response = await fetch(`${API_BASE_URL}/user-preferences`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Ultron-Cloud-Appid': APP_ID,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // ✅ Don't logout here! Just throw error - let caller decide
        // Network errors or temporary failures shouldn't cause immediate logout
        logger.warn('⚠️ Token refresh failed in getUserPreferences - NOT logging out');
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get user preferences: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    logger.log('✅ User preferences loaded:', data);

    // ============================================================
    // Cross-tenant validation
    // ============================================================
    const tokenTenantId = getTenantIdFromToken();
    if (tokenTenantId) {
      // Check if response includes tenantId for validation
      if (data.tenantId || data.userId) {
        const responseTenantId = data.tenantId || data.userId;

        if (tokenTenantId !== responseTenantId) {
          logger.error('❌ Tenant ID mismatch detected!');
          logger.error(`   Token tenant ID: ${tokenTenantId}`);
          logger.error(`   Response tenant ID: ${responseTenantId}`);
          throw new Error('Cross-tenant data access detected. Please re-login.');
        } else {
          logger.log(`✅ Tenant validation passed: ${tokenTenantId}`);
        }
      } else {
        // Backend doesn't return tenantId - this is normal, silently continue
        // (No warning needed - validation is optional security feature)
      }
    } else {
      // Could not extract tenant ID from token (opaque token format) - this is normal and expected
      // (No warning needed - opaque tokens don't contain decoded tenant IDs)
    }

      // Update cache with result
      const result = data.preferences || {};
      preferencesCache = { data: result, timestamp: Date.now() };
      return result;
    } catch (error) {
      logger.error('❌ Failed to get user preferences:', error);
      throw error;
    } finally {
      pendingRequest = null;
    }
  })();

  return pendingRequest;
}

/**
 * Save user preferences to Firestore
 */
export async function saveUserPreferences(preferences: UserPreferences): Promise<void> {
  try {
    let accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    let response = await fetch(`${API_BASE_URL}/user-preferences`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Ultron-Cloud-Appid': APP_ID,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(preferences)
    });

    // Handle 401 - attempt automatic token refresh
    if (response.status === 401) {
      logger.warn('⚠️ 401 Unauthorized in saveUserPreferences - Attempting token refresh...');
      const { authService } = await import('./authService');
      const refreshed = await authService.refreshToken();

      if (refreshed) {
        // Retry with new token
        accessToken = getAccessToken();
        response = await fetch(`${API_BASE_URL}/user-preferences`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Ultron-Cloud-Appid': APP_ID,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(preferences)
        });
      } else {
        // ✅ Don't logout here! Just throw error - let caller decide
        // Network errors or temporary failures shouldn't cause immediate logout
        logger.warn('⚠️ Token refresh failed in saveUserPreferences - NOT logging out');
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save user preferences: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    logger.log('✅ User preferences saved:', data);

    // Invalidate cache to ensure next read gets fresh data
    invalidatePreferencesCache();
  } catch (error) {
    logger.error('❌ Failed to save user preferences:', error);
    throw error;
  }
}

/**
 * Get virtual zones for a specific board type
 * Migration: Reads from virtualZones first, falls back to virtualGroups (legacy) if empty
 * @param boardType Board type - only 'intelliSpace' is active (legacy types kept for data migration)
 */
export async function getVirtualZones(boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace'): Promise<DeviceZone[]> {
  try {
    const preferences = await getUserPreferences();
    
    // Try new field first
    const zones = preferences.virtualZones?.[boardType];
    if (zones && zones.length > 0) {
      return zones;
    }
    
    // Fallback to legacy field (virtualGroups) for backward compatibility
    const legacyGroups = preferences.virtualGroups?.[boardType];
    if (legacyGroups && legacyGroups.length > 0) {
      logger.log(`🔄 [Migration] Found legacy virtualGroups for ${boardType}, will migrate on next save`);
      return legacyGroups;
    }
    
    return [];
  } catch (error) {
    logger.error(`Failed to get virtual zones for ${boardType}:`, error);
    return [];
  }
}

/**
 * Save virtual zones for a specific board type
 * Always saves to virtualZones (new field), clears virtualGroups (legacy) on save
 * Note: Only 'intelliSpace' should be used for new data (legacy types kept for data migration)
 */
export async function saveVirtualZones(
  boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace',
  zones: DeviceZone[]
): Promise<void> {
  try {
    // Get current preferences
    const preferences = await getUserPreferences();

    // Update virtual zones for this board type (new field)
    // Also clear the legacy virtualGroups field for this boardType to complete migration
    const updatedVirtualGroups = { ...preferences.virtualGroups };
    if (updatedVirtualGroups[boardType]) {
      delete updatedVirtualGroups[boardType];
      logger.log(`🔄 [Migration] Clearing legacy virtualGroups.${boardType} after migration to virtualZones`);
    }

    const updatedPreferences: UserPreferences = {
      ...preferences,
      virtualZones: {
        ...preferences.virtualZones,
        [boardType]: zones
      },
      virtualGroups: Object.keys(updatedVirtualGroups).length > 0 ? updatedVirtualGroups : undefined
    };

    // Save back to Firestore
    await saveUserPreferences(updatedPreferences);

    logger.log(`✅ Virtual zones saved for ${boardType}:`, zones);
  } catch (error) {
    logger.error(`Failed to save virtual zones for ${boardType}:`, error);
    throw error;
  }
}

// ============================================================
// Legacy Functions (kept for backward compatibility)
// ============================================================

/**
 * @deprecated Use getVirtualZones instead
 * Get virtual groups for a specific board type
 */
export async function getVirtualGroups(boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace'): Promise<DeviceZone[]> {
  return getVirtualZones(boardType);
}

/**
 * @deprecated Use saveVirtualZones instead
 * Save virtual groups for a specific board type
 */
export async function saveVirtualGroups(
  boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace',
  groups: DeviceZone[]
): Promise<void> {
  return saveVirtualZones(boardType, groups);
}

/**
 * Migrate virtual zones from localStorage to Firestore (OLD FORMAT)
 * This is for old user-email-based keys: device-groups-${boardType}-${userEmail}
 */
export async function migrateFromLocalStorage(): Promise<void> {
  try {
    // Only migrate intelliSpace (legacy boards removed)
    const tokensStr = sessionStorage.getItem('ultron_tokens');
    if (!tokensStr) return;

    const tokens = JSON.parse(tokensStr);
    const userEmail = tokens.userInfo?.email || 'anonymous';

    const oldKey = `device-groups-intelliSpace-${userEmail}`;
    const oldData = localStorage.getItem(oldKey);

    if (oldData) {
      try {
        const zones = JSON.parse(oldData);
        if (zones && zones.length > 0) {
          await saveVirtualZones('intelliSpace', zones);
          logger.log(`✅ Migrated ${zones.length} zones from localStorage for intelliSpace`);
          // Remove old localStorage data after successful migration
          localStorage.removeItem(oldKey);
          logger.log('✅ Migration from localStorage completed');
        }
      } catch (error) {
        logger.error('Failed to migrate intelliSpace:', error);
      }
    } else {
      logger.log('ℹ️ No data to migrate from localStorage');
    }
  } catch (error) {
    logger.error('Migration failed:', error);
  }
}

/**
 * Migrate virtual zones from localStorage to Firestore (CURRENT FORMAT)
 * This handles current localStorage keys: device-zones-${boardType} (new) and device-groups-${boardType} (legacy)
 * Only migrates if Firestore has no data (prevents overwriting existing Firestore data)
 */
export async function migrateVirtualZonesIfNeeded(): Promise<void> {
  try {
    logger.log('🔄 Checking for virtual zones migration...');

    // Only migrate intelliSpace (legacy boards removed)
    // Check new key first, then legacy key
    const newLocalStorageKey = 'device-zones-intelliSpace';
    const legacyLocalStorageKey = 'device-groups-intelliSpace';
    
    let localData = localStorage.getItem(newLocalStorageKey);
    let sourceKey = newLocalStorageKey;
    
    if (!localData) {
      localData = localStorage.getItem(legacyLocalStorageKey);
      sourceKey = legacyLocalStorageKey;
    }

    if (localData) {
      try {
        const localZones = JSON.parse(localData);

        // Check if Firestore already has data for intelliSpace
        const firestoreZones = await getVirtualZones('intelliSpace');

        if (firestoreZones.length === 0 && localZones.length > 0) {
          // Firestore is empty but localStorage has data - migrate!
          await saveVirtualZones('intelliSpace', localZones);
          logger.log(`✅ Migrated ${localZones.length} virtual zones from localStorage (${sourceKey}) to Firestore for intelliSpace`);
          
          // Clean up legacy localStorage key if we migrated from it
          if (sourceKey === legacyLocalStorageKey) {
            localStorage.removeItem(legacyLocalStorageKey);
            logger.log('🧹 Removed legacy localStorage key: device-groups-intelliSpace');
          }
        } else if (firestoreZones.length > 0) {
          logger.log(`ℹ️ intelliSpace: Firestore already has ${firestoreZones.length} zones, skipping migration`);
        } else {
          logger.log('ℹ️ intelliSpace: No zones to migrate');
        }
      } catch (error) {
        logger.error('Failed to migrate intelliSpace:', error);
      }
    } else {
      logger.log('ℹ️ No virtual zones migration needed');
    }
  } catch (error) {
    logger.error('Virtual zones migration failed:', error);
    // Don't throw - migration failure shouldn't break app startup
  }
}

/**
 * @deprecated Use migrateVirtualZonesIfNeeded instead
 */
export async function migrateVirtualGroupsIfNeeded(): Promise<void> {
  return migrateVirtualZonesIfNeeded();
}

/**
 * Get imported devices for a specific board type
 * ✅ 規劃原則：importedDevices 只存 deviceSN，其他資料從 device shadow 獲取
 * 自動遷移舊格式資料（清理 nickname, type, groupId 等欄位）
 * Note: Only 'intelliSpace' should be used (legacy types kept for data migration)
 */
export async function getImportedDevices(boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace'): Promise<Device[]> {
  try {
    const preferences = await getUserPreferences();
    const devices = preferences.importedDevices?.[boardType] || [];
    
    // ✅ 遷移邏輯：如果發現舊格式（有 nickname 等欄位），自動清理為只保留 deviceSN
    let needsMigration = false;
    const cleanedDevices = devices.map((d: any) => {
      const deviceSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
      
      // 檢查是否為舊格式（有額外欄位）
      const hasExtraFields = d.nickname !== undefined || d.type !== undefined || 
                             d.groupId !== undefined || d.displayName !== undefined ||
                             d.name !== undefined || d.deviceName !== undefined ||
                             d.batteryLevel !== undefined || d.hasBattery !== undefined ||
                             d.status !== undefined || d.traits !== undefined ||
                             d.attributes !== undefined;
      
      if (hasExtraFields && deviceSN) {
        needsMigration = true;
        // ✅ 規劃原則：只存 deviceSN，但同時設置 sn 以保持向後兼容
        // 這樣舊代碼使用 device.sn 和新代碼使用 device.deviceSN 都能正常工作
        return {
          deviceSN: deviceSN,
          sn: deviceSN  // ✅ 向後兼容：保留 sn 字段
        } as any as Device;
      }
      
      // 如果沒有 deviceSN，嘗試從 sn 或 deviceId 提取
      if (!deviceSN) {
        logger.warn(`⚠️ [Migration] Device missing deviceSN:`, d);
        return d;
      }
      
      return d;
    });
    
    // 如果有需要遷移的資料，自動存回 Firestore
    if (needsMigration) {
      logger.log(`🔄 [Migration] Cleaning ${boardType} importedDevices: removing extra fields, keeping only deviceSN`);
      try {
        await saveImportedDevices(boardType, cleanedDevices);
        logger.log(`✅ [Migration] Migrated ${boardType} importedDevices to simplified format`);
      } catch (error) {
        logger.error(`❌ [Migration] Failed to save migrated devices:`, error);
        // 繼續返回清理後的資料，即使保存失敗
      }
    }
    
    return cleanedDevices;
  } catch (error) {
    logger.error(`Failed to get imported devices for ${boardType}:`, error);
    return [];
  }
}

/**
 * Check if a device is used in any other board's importedDevices or Virtual Groups
 * Used when removing a device from a Virtual Group to determine if it should be removed from webhook subscriptions
 * @param deviceSN - The device SN to check
 * @param excludeBoard - The board to exclude from the check (current board being operated on)
 * @returns true if the device is used elsewhere, false if it can be safely removed
 */
/**
 * Check if a device is used in any other board
 * @param deviceSN Device serial number
 * @param excludeBoard Board to exclude from check
 */
export async function isDeviceUsedAnywhereElse(
  deviceSN: string,
  excludeBoard: 'intelliSpace'
): Promise<boolean> {
  // Only intelliSpace board exists now, so if we're excluding it, nothing else to check
  if (excludeBoard === 'intelliSpace') {
    return false;
  }

  try {
    // Check importedDevices for intelliSpace
    const importedDevices = await getImportedDevices('intelliSpace');
    if (importedDevices.some(d => {
      const dSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
      return dSN === deviceSN;
    })) {
      logger.log(`📋 Device ${deviceSN} found in intelliSpace importedDevices`);
      return true;
    }

    // Check Virtual Zones for intelliSpace
    const zones = await getVirtualZones('intelliSpace');
    for (const zone of zones) {
      if (zone.devices?.includes(deviceSN)) {
        logger.log(`📋 Device ${deviceSN} found in intelliSpace virtual zone: ${zone.name}`);
        return true;
      }
    }
  } catch (error) {
    logger.error('Failed to check device usage for intelliSpace:', error);
  }

  return false;
}

/**
 * Helper function to deduplicate devices based on deviceSN
 * Keeps the first occurrence of each device
 * ✅ 規劃原則：使用 deviceSN 作為唯一識別符
 */
function deduplicateDevices(devices: Device[]): Device[] {
  const seen = new Set<string>();
  const uniqueDevices: Device[] = [];

  for (const device of devices) {
    const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
    if (deviceSN && !seen.has(deviceSN)) {
      seen.add(deviceSN);
      uniqueDevices.push(device);
    }
  }

  return uniqueDevices;
}

/**
 * Save imported devices for a specific board type
 * ✅ 規劃原則：只存 deviceSN，其他資料（nickname, type, groupId 等）從 device shadow 獲取
 * Automatically deduplicates devices based on deviceSN before saving
 * Note: Only 'intelliSpace' should be used (legacy types kept for data migration)
 */
export async function saveImportedDevices(
  boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace',
  devices: Device[]
): Promise<void> {
  try {
    // Get current preferences
    const preferences = await getUserPreferences();

    // ✅ Deduplicate devices based on deviceSN (keeps first occurrence)
    const uniqueDevices = deduplicateDevices(devices);

    if (uniqueDevices.length !== devices.length) {
      logger.log(`🔄 Deduplicated ${devices.length - uniqueDevices.length} duplicate devices for ${boardType}`);
    }

    // ✅ 規劃原則：只存 deviceSN，清理所有其他欄位，但保留 sn 向後兼容
    const simplifiedDevices = uniqueDevices.map((d: any) => {
      const deviceSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
      if (!deviceSN) {
        logger.warn(`⚠️ [SaveImportedDevices] Device missing deviceSN:`, d);
        return d; // 如果沒有 deviceSN，保留原樣（向後兼容）
      }
      
      // ✅ 規劃原則：只存 deviceSN，但同時設置 sn 以保持向後兼容
      return {
        deviceSN: deviceSN,
        sn: deviceSN  // ✅ 向後兼容：保留 sn 字段
      } as any as Device;
    });

    // Update imported devices for this board type
    const updatedPreferences: UserPreferences = {
      ...preferences,
      importedDevices: {
        ...preferences.importedDevices,
        [boardType]: simplifiedDevices
      }
    };

    // Save back to Firestore
    await saveUserPreferences(updatedPreferences);

    logger.log(`✅ Imported devices saved to Firestore for ${boardType}:`, simplifiedDevices.length, 'devices (simplified to deviceSN only)');
  } catch (error) {
    logger.error(`Failed to save imported devices for ${boardType}:`, error);
    throw error;
  }
}

// ============================================================
// Dashboard Settings
// ============================================================

/**
 * Get dashboard settings from Firestore
 */
export async function getDashboardSettings(): Promise<DashboardSettings | null> {
  try {
    const preferences = await getUserPreferences();
    return preferences.dashboardSettings || null;
  } catch (error) {
    logger.error('Failed to get dashboard settings:', error);
    return null;
  }
}

/**
 * Save dashboard settings to Firestore
 */
export async function saveDashboardSettings(settings: DashboardSettings): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      dashboardSettings: settings
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Dashboard settings saved to Firestore');
  } catch (error) {
    logger.error('Failed to save dashboard settings:', error);
    throw error;
  }
}

// ============================================================
// Theme & Language
// ============================================================

/**
 * Get theme from Firestore
 */
export async function getTheme(): Promise<'light' | 'dark' | null> {
  try {
    const preferences = await getUserPreferences();
    return preferences.theme || null;
  } catch (error) {
    logger.error('Failed to get theme:', error);
    return null;
  }
}

/**
 * Save theme to Firestore
 */
export async function saveTheme(theme: 'light' | 'dark'): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      theme
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Theme saved to Firestore:', theme);
  } catch (error) {
    logger.error('Failed to save theme:', error);
    throw error;
  }
}

/**
 * Get language from Firestore
 */
export async function getLanguage(): Promise<'zh-TW' | 'en-US' | null> {
  try {
    const preferences = await getUserPreferences();
    return preferences.language || null;
  } catch (error) {
    logger.error('Failed to get language:', error);
    return null;
  }
}

/**
 * Save language to Firestore
 */
export async function saveLanguage(language: 'zh-TW' | 'en-US'): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      language
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Language saved to Firestore:', language);
  } catch (error) {
    logger.error('Failed to save language:', error);
    throw error;
  }
}

// ============================================================
// Device Order
// ============================================================

/**
 * Get device order for a specific board
 * Note: Only 'intelliSpace' should be used (legacy types kept for data migration)
 */
export async function getDeviceOrder(boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace'): Promise<string[]> {
  try {
    const preferences = await getUserPreferences();
    return preferences.deviceOrder?.[boardType] || [];
  } catch (error) {
    logger.error(`Failed to get device order for ${boardType}:`, error);
    return [];
  }
}

/**
 * Save device order for a specific board
 * Note: Only 'intelliSpace' should be used (legacy types kept for data migration)
 */
export async function saveDeviceOrder(
  boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace',
  order: string[]
): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      deviceOrder: {
        ...preferences.deviceOrder,
        [boardType]: order
      }
    };
    await saveUserPreferences(updatedPreferences);
    logger.log(`✅ Device order saved for ${boardType}:`, order.length, 'devices');
  } catch (error) {
    logger.error(`Failed to save device order for ${boardType}:`, error);
    throw error;
  }
}

// ============================================================
// Company Branding
// ============================================================

/**
 * Get company branding from Firestore
 */
export async function getCompanyBranding(): Promise<CompanyBranding | null> {
  try {
    const preferences = await getUserPreferences();
    return preferences.companyBranding || null;
  } catch (error) {
    logger.error('Failed to get company branding:', error);
    return null;
  }
}

/**
 * Save company branding to Firestore
 */
export async function saveCompanyBranding(branding: CompanyBranding): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      companyBranding: branding
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Company branding saved to Firestore');
  } catch (error) {
    logger.error('Failed to save company branding:', error);
    throw error;
  }
}

// ============================================================
// Battery Alert
// ============================================================

/**
 * Get battery alert settings from Firestore
 */
export async function getBatteryAlert(): Promise<BatteryAlertSettings | null> {
  try {
    const preferences = await getUserPreferences();
    return preferences.batteryAlert || null;
  } catch (error) {
    logger.error('Failed to get battery alert settings:', error);
    return null;
  }
}

/**
 * Save battery alert settings to Firestore
 */
export async function saveBatteryAlert(settings: BatteryAlertSettings): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      batteryAlert: settings
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Battery alert settings saved to Firestore');
  } catch (error) {
    logger.error('Failed to save battery alert settings:', error);
    throw error;
  }
}

// ============================================================
// Migration: Migrate ALL localStorage data to Firestore
// ============================================================

/**
 * Migrate all user preferences from localStorage to Firestore
 * This is a comprehensive one-time migration helper
 */
export async function migrateAllFromLocalStorage(): Promise<{
  migrated: string[];
  failed: string[];
}> {
  const migrated: string[] = [];
  const failed: string[] = [];

  try {
    // Get current Firestore preferences as base
    let preferences: UserPreferences = {};
    try {
      preferences = await getUserPreferences();
    } catch {
      // Start fresh if no preferences exist
    }

    // 1. Migrate theme
    const theme = localStorage.getItem('ultron-energy-theme');
    if (theme && (theme === 'light' || theme === 'dark')) {
      preferences.theme = theme;
      migrated.push('theme');
    }

    // 2. Migrate language
    const language = localStorage.getItem('ultron-language');
    if (language && (language === 'zh-TW' || language === 'en-US')) {
      preferences.language = language as 'zh-TW' | 'en-US';
      migrated.push('language');
    }

    // 3. Migrate dashboard settings
    const settingsStr = localStorage.getItem('ultron-energy-settings');
    if (settingsStr) {
      try {
        const settings = JSON.parse(settingsStr);
        preferences.dashboardSettings = {
          powerMeterRefreshInterval: settings.powerMeterRefreshInterval || 30,
          powerChartRefreshInterval: settings.powerChartRefreshInterval || 1800,
          showOfflineDevices: settings.showOfflineDevices !== false,
          autoRefresh: settings.autoRefresh !== false,
          demoMode: settings.demoMode || false,
          timezone: settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          keepLoggedIn: settings.keepLoggedIn || false
        };
        migrated.push('dashboardSettings');
      } catch {
        failed.push('dashboardSettings');
      }
    }

    // 4. Migrate company branding
    const logo = localStorage.getItem('ultron-company-logo');
    const favicon = localStorage.getItem('ultron-company-favicon');
    const companyName = localStorage.getItem('ultron-company-name');
    if (logo || favicon || companyName) {
      preferences.companyBranding = {
        logo: logo || undefined,
        favicon: favicon || undefined,
        companyName: companyName || undefined
      };
      migrated.push('companyBranding');
    }

    // 5. Migrate battery alert
    const batteryAlertStr = localStorage.getItem('ultron-battery-alert');
    if (batteryAlertStr) {
      try {
        const batteryAlert = JSON.parse(batteryAlertStr);
        preferences.batteryAlert = {
          enabled: batteryAlert.enabled || false,
          threshold: batteryAlert.threshold || 20
        };
        migrated.push('batteryAlert');
      } catch {
        failed.push('batteryAlert');
      }
    }

    // 6. ✅ Skip legacy device order migration (energy/climate/hvac)
    // Only intelliSpace is used now - legacy localStorage keys will be cleaned up
    // by removing them from localStorage (cleanup below)
    const legacyOrderKeys = ['power-meter-order', 'climateDeviceOrder', 'hvacDeviceOrder'];
    legacyOrderKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        logger.log(`🧹 Removed legacy localStorage key: ${key}`);
      }
    });

    // Save all migrated preferences to Firestore
    if (migrated.length > 0) {
      await saveUserPreferences(preferences);
      logger.log('✅ Migration completed:', migrated);

      // Optionally clear migrated localStorage keys
      // Uncomment if you want to clean up after successful migration
      // localStorage.removeItem('ultron-energy-theme');
      // localStorage.removeItem('ultron-language');
      // etc.
    }

    return { migrated, failed };
  } catch (error) {
    logger.error('Migration failed:', error);
    return { migrated, failed: [...failed, 'unknown'] };
  }
}

/**
 * Load all preferences from Firestore and apply to localStorage (for backward compatibility)
 * Call this on login to sync Firestore → localStorage
 */
export async function loadPreferencesToLocalStorage(): Promise<void> {
  try {
    const preferences = await getUserPreferences();

    // Apply theme - ✅ localStorage 優先策略
    // 如果 localStorage 已有主題設定，以 localStorage 為準（用戶本地設定優先）
    // 並同步到 Firestore（確保跨設備同步）
    const localTheme = localStorage.getItem('ultron-energy-theme') as 'light' | 'dark' | null;
    if (localTheme && localTheme !== preferences.theme) {
      // localStorage 有不同的主題，同步到 Firestore
      try {
        await saveUserPreferences({ ...preferences, theme: localTheme });
        console.log(`✅ Synced local theme "${localTheme}" to Firestore`);
      } catch (err) {
        console.warn('Failed to sync local theme to Firestore:', err);
      }
    } else if (!localTheme && preferences.theme) {
      // localStorage 沒有主題，從 Firestore 載入
      localStorage.setItem('ultron-energy-theme', preferences.theme);
      window.dispatchEvent(new CustomEvent('theme-loaded-from-firestore', { 
        detail: { theme: preferences.theme } 
      }));
    }

    // Apply language
    if (preferences.language) {
      localStorage.setItem('ultron-language', preferences.language);
    }

    // Apply dashboard settings
    if (preferences.dashboardSettings) {
      localStorage.setItem('ultron-energy-settings', JSON.stringify(preferences.dashboardSettings));
    }

    // Apply company branding
    if (preferences.companyBranding) {
      if (preferences.companyBranding.logo) {
        localStorage.setItem('ultron-company-logo', preferences.companyBranding.logo);
      }
      if (preferences.companyBranding.favicon) {
        localStorage.setItem('ultron-company-favicon', preferences.companyBranding.favicon);
      }
      if (preferences.companyBranding.companyName) {
        localStorage.setItem('ultron-company-name', preferences.companyBranding.companyName);
      }
    }

    // Apply battery alert
    if (preferences.batteryAlert) {
      localStorage.setItem('ultron-battery-alert', JSON.stringify(preferences.batteryAlert));
    }

    // ✅ Device order - only intelliSpace is used now (legacy boards removed)
    // No need to sync to localStorage as intelliSpace uses Firestore directly

    // Apply imported devices for intelliSpace (legacy boards removed)
    if (preferences.importedDevices?.intelliSpace) {
      const key = 'imported-devices-intelliSpace';
      localStorage.setItem(key, JSON.stringify(preferences.importedDevices.intelliSpace));
      logger.log('✅ Synced imported devices for intelliSpace to localStorage');
    }

    logger.log('✅ Preferences loaded from Firestore to localStorage');
  } catch (error) {
    logger.error('Failed to load preferences to localStorage:', error);
  }
}

// ============================================================
// Orphaned Data Cleanup
// ============================================================

/**
 * Cleanup orphaned device references from preferences
 * Removes deleted device SNs from deviceOrder and selectedDevices
 *
 * @param boardType Board type - only 'intelliSpace' is active (legacy types kept for data migration)
 * @param validDeviceSNs Set of currently imported device SNs
 * @returns Number of references cleaned up
 */
export async function cleanupOrphanedDeviceReferences(
  boardType: 'intelliSpace' | 'climate' | 'energy' | 'lighting' | 'hvac' = 'intelliSpace',
  validDeviceSNs: Set<string>
): Promise<number> {
  let cleanedCount = 0;

  try {
    logger.log(`🧹 [Cleanup] Checking for orphaned references in ${boardType}...`);

    // Get current preferences
    const preferences = await getUserPreferences();
    let updated = false;

    // 1. Clean deviceOrder
    const deviceOrder = preferences.deviceOrder;
    if (deviceOrder && deviceOrder[boardType]) {
      const currentOrder: string[] = deviceOrder[boardType] as string[];
      const cleanedOrder: string[] = currentOrder.filter((sn: string) => validDeviceSNs.has(sn));

      if (cleanedOrder.length !== currentOrder.length) {
        const removed = currentOrder.length - cleanedOrder.length;
        logger.log(`🧹 [Cleanup] Removed ${removed} orphaned SNs from deviceOrder.${boardType}`);

        deviceOrder[boardType] = cleanedOrder;
        cleanedCount += removed;
        updated = true;
      }
    }

    // 2. Clean selectedDevices
    const selectedDevices = preferences.selectedDevices;
    if (selectedDevices) {
      // Clean board-specific selections
      if (selectedDevices[boardType as keyof SelectedDevices]) {
        const currentSelected: string[] = selectedDevices[boardType as keyof SelectedDevices] as string[];
        const cleanedSelected: string[] = currentSelected.filter((sn: string) => validDeviceSNs.has(sn));

        if (cleanedSelected.length !== currentSelected.length) {
          const removed = currentSelected.length - cleanedSelected.length;
          logger.log(`🧹 [Cleanup] Removed ${removed} orphaned SNs from selectedDevices.${boardType}`);

          (selectedDevices[boardType as keyof SelectedDevices] as string[]) = cleanedSelected;
          cleanedCount += removed;
          updated = true;
        }
      }

      // Clean power24h selections if applicable
      if (selectedDevices.power24h) {
        const currentPower24h: string[] = selectedDevices.power24h;
        const cleanedPower24h: string[] = currentPower24h.filter((sn: string) => validDeviceSNs.has(sn));

        if (cleanedPower24h.length !== currentPower24h.length) {
          const removed = currentPower24h.length - cleanedPower24h.length;
          logger.log(`🧹 [Cleanup] Removed ${removed} orphaned SNs from selectedDevices.power24h`);

          selectedDevices.power24h = cleanedPower24h;
          cleanedCount += removed;
          updated = true;
        }
      }
    }

    // Save updated preferences if any cleanup was done
    if (updated) {
      await saveUserPreferences(preferences);
      logger.log(`✅ [Cleanup] Cleaned ${cleanedCount} orphaned references for ${boardType}`);
    } else {
      logger.log(`✅ [Cleanup] No orphaned references found for ${boardType}`);
    }

    return cleanedCount;
  } catch (error) {
    logger.error(`❌ [Cleanup] Failed to cleanup orphaned references for ${boardType}:`, error);
    // Non-throwing - don't break device removal on cleanup failure
    return cleanedCount;
  }
}

/**
 * 清理孤立的 importedDevices
 * 移除不在任何 Virtual Zone 中的設備
 * @returns 清理報告 { cleanedCount, cleanedDevices }
 */
export async function cleanupOrphanedImportedDevices(): Promise<{
  cleanedCount: number;
  cleanedDevices: string[];
}> {
  let totalCleaned = 0;
  const allCleanedDevices: string[] = [];

  try {
    logger.log('🧹 [Cleanup] Starting orphaned importedDevices cleanup...');

    // 1. 收集所有 Virtual Zones 中使用的設備 SN (intelliSpace only)
    const usedDeviceSNs = new Set<string>();
    const zones = await getVirtualZones('intelliSpace');
    for (const zone of zones) {
      zone.devices?.forEach(sn => usedDeviceSNs.add(sn));
    }
    logger.log(`📊 [Cleanup] Found ${usedDeviceSNs.size} devices in Virtual Zones`);

    // 2. 對 intelliSpace 的 importedDevices 進行清理
    const importedDevices = await getImportedDevices('intelliSpace');
    const cleanedDevices = importedDevices.filter(d => {
      const dSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
      return dSN && usedDeviceSNs.has(dSN);
    });

    const orphanedCount = importedDevices.length - cleanedDevices.length;
    if (orphanedCount > 0) {
      const orphanedSNs = importedDevices
        .filter(d => {
          const dSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
          return dSN && !usedDeviceSNs.has(dSN);
        })
        .map(d => (d as any).deviceSN || d.sn || (d as any).deviceId)
        .filter((sn): sn is string => !!sn);

      await saveImportedDevices('intelliSpace', cleanedDevices);
      logger.log(`🧹 [Cleanup] intelliSpace: 移除 ${orphanedCount} 個孤立設備: ${orphanedSNs.join(', ')}`);

      totalCleaned += orphanedCount;
      allCleanedDevices.push(...orphanedSNs);
    }

    // 3. 同步 webhook_devices
    if (totalCleaned > 0) {
      const { ultronTuringApi } = await import('./ultronTuringApi');
      await ultronTuringApi.syncAllImportedDevicesToWebhook();
      logger.log(`✅ [Cleanup] Webhook 已同步，共清理 ${totalCleaned} 個設備`);
    } else {
      logger.log(`✅ [Cleanup] No orphaned importedDevices found`);
    }

    return { cleanedCount: totalCleaned, cleanedDevices: allCleanedDevices };
  } catch (error) {
    logger.error('❌ [Cleanup] Failed to cleanup orphaned importedDevices:', error);
    return { cleanedCount: totalCleaned, cleanedDevices: allCleanedDevices };
  }
}

// ============================================================
// WebSocket Enabled Devices
// ============================================================

/**
 * Set websocket enabled devices
 * 更新 Firestore 中的 websocketEnabledDevices 欄位
 * 用於 Layer 2 (WebSocket) 訂閱管理
 */
export async function setWebsocketEnabledDevices(deviceSNs: string[]): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      websocketEnabledDevices: deviceSNs
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ websocketEnabledDevices saved:', deviceSNs.length, 'devices');
  } catch (error) {
    logger.error('Failed to set websocketEnabledDevices:', error);
    throw error;
  }
}

/**
 * Get websocket enabled devices
 * 從 Firestore 獲取已啟用 WebSocket 的設備列表
 */
export async function getWebsocketEnabledDevices(): Promise<string[]> {
  try {
    const preferences = await getUserPreferences();
    return preferences.websocketEnabledDevices || [];
  } catch (error) {
    logger.error('Failed to get websocketEnabledDevices:', error);
    return [];
  }
}

// ============================================================
// Device Categories
// ============================================================

/**
 * Get device categories (legacy - kept for backward compatibility)
 * All devices are now managed under intelliSpace
 */
export async function getDeviceCategories(): Promise<Record<string, string>> {
  try {
    const preferences = await getUserPreferences();
    return preferences.deviceCategories || {};
  } catch (error) {
    logger.error('Failed to get device categories:', error);
    return {};
  }
}

/**
 * Save device categories to Firestore (legacy - kept for backward compatibility)
 * All devices are now managed under intelliSpace
 */
export async function saveDeviceCategories(categories: Record<string, string>): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      deviceCategories: categories
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Device categories saved successfully');
  } catch (error) {
    logger.error('Failed to save device categories:', error);
    throw error;
  }
}

// ============================================================
// IntelliSpace Selections
// ============================================================

/**
 * Get IntelliSpace device selections from Firestore
 */
export async function getIntelliSpaceSelections(): Promise<IntelliSpaceSelections> {
  try {
    const preferences = await getUserPreferences();
    return preferences.intelliSpaceSelections || {};
  } catch (error) {
    logger.error('Failed to get IntelliSpace selections:', error);
    return {};
  }
}

/**
 * Save IntelliSpace device selections to Firestore
 */
export async function saveIntelliSpaceSelections(selections: IntelliSpaceSelections): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      intelliSpaceSelections: selections
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ IntelliSpace selections saved to Firestore:', selections);
  } catch (error) {
    logger.error('Failed to save IntelliSpace selections:', error);
    throw error;
  }
}

// ============================================================
// Multi-Channel Display Mode
// ============================================================

/**
 * Get multi-channel display mode for a specific device
 * @param baseSN - The base serial number of the device (without endpoint suffix)
 * @returns 'merged' (default) or 'expanded'
 */
export async function getMultiChannelDisplayMode(baseSN: string): Promise<'merged' | 'expanded'> {
  try {
    const preferences = await getUserPreferences();
    const modes = preferences.multiChannelDisplayMode || {};
    return modes[baseSN] || 'expanded'; // Default to expanded mode (split channels)
  } catch (error) {
    logger.error('Failed to get multi-channel display mode:', error);
    return 'expanded';
  }
}

/**
 * Get all multi-channel display mode preferences
 * @returns Record of baseSN -> display mode
 */
export async function getAllMultiChannelDisplayModes(): Promise<Record<string, 'merged' | 'expanded'>> {
  try {
    const preferences = await getUserPreferences();
    return preferences.multiChannelDisplayMode || {};
  } catch (error) {
    logger.error('Failed to get multi-channel display modes:', error);
    return {};
  }
}

/**
 * Set multi-channel display mode for a specific device
 * @param baseSN - The base serial number of the device (without endpoint suffix)
 * @param mode - 'merged' or 'expanded'
 */
export async function setMultiChannelDisplayMode(baseSN: string, mode: 'merged' | 'expanded'): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const currentModes = preferences.multiChannelDisplayMode || {};
    
    const updatedPreferences: UserPreferences = {
      ...preferences,
      multiChannelDisplayMode: {
        ...currentModes,
        [baseSN]: mode
      }
    };
    
    await saveUserPreferences(updatedPreferences);
    logger.log(`✅ Multi-channel display mode set: ${baseSN} → ${mode}`);
  } catch (error) {
    logger.error('Failed to set multi-channel display mode:', error);
    throw error;
  }
}

// ============================================================
// Device Control Groups (Apple Home style group control)
// ============================================================

/**
 * Get all device control groups
 * @returns Array of DeviceControlGroup
 */
export async function getDeviceControlGroups(): Promise<DeviceControlGroup[]> {
  try {
    const preferences = await getUserPreferences();
    return preferences.deviceControlGroups || [];
  } catch (error) {
    logger.error('Failed to get device control groups:', error);
    return [];
  }
}

/**
 * Save all device control groups
 * @param groups - Array of DeviceControlGroup
 */
export async function saveDeviceControlGroups(groups: DeviceControlGroup[]): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const updatedPreferences: UserPreferences = {
      ...preferences,
      deviceControlGroups: groups
    };
    await saveUserPreferences(updatedPreferences);
    logger.log('✅ Device control groups saved:', groups.length, 'groups');
  } catch (error) {
    logger.error('Failed to save device control groups:', error);
    throw error;
  }
}

/**
 * Create a new device control group
 * @param name - Group name
 * @param zoneId - Parent Zone ID
 * @param deviceType - Device type (light, ac, etc.)
 * @param devices - Array of deviceSNs
 * @returns The created DeviceControlGroup
 */
export async function createDeviceControlGroup(
  name: string,
  zoneId: string,
  deviceType: string,
  devices: string[]
): Promise<DeviceControlGroup> {
  try {
    const groups = await getDeviceControlGroups();
    
    const newGroup: DeviceControlGroup = {
      id: `dcg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      name,
      zoneId,
      deviceType,
      devices,
      createdAt: new Date().toISOString()
    };
    
    groups.push(newGroup);
    await saveDeviceControlGroups(groups);
    
    logger.log('✅ Device control group created:', newGroup.id, newGroup.name);
    return newGroup;
  } catch (error) {
    logger.error('Failed to create device control group:', error);
    throw error;
  }
}

/**
 * Update a device control group
 * @param groupId - Group ID to update
 * @param updates - Partial updates to apply
 * @returns The updated DeviceControlGroup or null if not found
 */
export async function updateDeviceControlGroup(
  groupId: string,
  updates: Partial<Omit<DeviceControlGroup, 'id' | 'createdAt'>>
): Promise<DeviceControlGroup | null> {
  try {
    const groups = await getDeviceControlGroups();
    const index = groups.findIndex(g => g.id === groupId);
    
    if (index === -1) {
      logger.warn('Device control group not found:', groupId);
      return null;
    }
    
    groups[index] = { ...groups[index], ...updates };
    await saveDeviceControlGroups(groups);
    
    logger.log('✅ Device control group updated:', groupId);
    return groups[index];
  } catch (error) {
    logger.error('Failed to update device control group:', error);
    throw error;
  }
}

/**
 * Delete a device control group (Ungroup)
 * @param groupId - Group ID to delete
 * @returns true if deleted, false if not found
 */
export async function deleteDeviceControlGroup(groupId: string): Promise<boolean> {
  try {
    const groups = await getDeviceControlGroups();
    const index = groups.findIndex(g => g.id === groupId);
    
    if (index === -1) {
      logger.warn('Device control group not found:', groupId);
      return false;
    }
    
    const deletedGroup = groups.splice(index, 1)[0];
    await saveDeviceControlGroups(groups);
    
    logger.log('✅ Device control group deleted (ungrouped):', groupId, deletedGroup.name);
    return true;
  } catch (error) {
    logger.error('Failed to delete device control group:', error);
    throw error;
  }
}

/**
 * Get device control groups for a specific zone
 * @param zoneId - Zone ID to filter by
 * @returns Array of DeviceControlGroup in that zone
 */
export async function getDeviceControlGroupsByZone(zoneId: string): Promise<DeviceControlGroup[]> {
  try {
    const groups = await getDeviceControlGroups();
    return groups.filter(g => g.zoneId === zoneId);
  } catch (error) {
    logger.error('Failed to get device control groups by zone:', error);
    return [];
  }
}

/**
 * Check if a device is in any control group
 * @param deviceSN - Device SN to check
 * @returns The DeviceControlGroup containing the device, or null
 */
export async function getDeviceControlGroupForDevice(deviceSN: string): Promise<DeviceControlGroup | null> {
  try {
    const groups = await getDeviceControlGroups();
    return groups.find(g => g.devices.includes(deviceSN)) || null;
  } catch (error) {
    logger.error('Failed to check device control group:', error);
    return null;
  }
}

/**
 * Add a device to a control group
 * @param groupId - Group ID
 * @param deviceSN - Device SN to add
 * @returns Updated group or null if not found
 */
export async function addDeviceToControlGroup(
  groupId: string,
  deviceSN: string
): Promise<DeviceControlGroup | null> {
  try {
    const groups = await getDeviceControlGroups();
    const group = groups.find(g => g.id === groupId);
    
    if (!group) {
      logger.warn('Device control group not found:', groupId);
      return null;
    }
    
    if (!group.devices.includes(deviceSN)) {
      group.devices.push(deviceSN);
      await saveDeviceControlGroups(groups);
      logger.log('✅ Device added to control group:', deviceSN, '→', group.name);
    }
    
    return group;
  } catch (error) {
    logger.error('Failed to add device to control group:', error);
    throw error;
  }
}

/**
 * Remove a device from a control group
 * @param groupId - Group ID
 * @param deviceSN - Device SN to remove
 * @returns Updated group or null if not found
 */
export async function removeDeviceFromControlGroup(
  groupId: string,
  deviceSN: string
): Promise<DeviceControlGroup | null> {
  try {
    const groups = await getDeviceControlGroups();
    const group = groups.find(g => g.id === groupId);
    
    if (!group) {
      logger.warn('Device control group not found:', groupId);
      return null;
    }
    
    const index = group.devices.indexOf(deviceSN);
    if (index !== -1) {
      group.devices.splice(index, 1);
      await saveDeviceControlGroups(groups);
      logger.log('✅ Device removed from control group:', deviceSN, '←', group.name);
    }
    
    return group;
  } catch (error) {
    logger.error('Failed to remove device from control group:', error);
    throw error;
  }
}
